import { Button, Space, Typography } from "antd";
import React from "react";

import {
  IForecastingCentralRepoRes,
  IForecastingRes
} from "@/types/forecasting";

const { Text } = Typography;

export const SelectRunPlotStep = ({
  projectCentralRepoData,
  monthForecastData,
  selectedPlots,
  handlePlotSelectionChange
}: {
  projectCentralRepoData: IForecastingCentralRepoRes;
  monthForecastData?: IForecastingRes;
  selectedPlots: Set<string>;
  handlePlotSelectionChange: (plots: Set<string>) => void;
}) => {
  const availablePlotNumbers = React.useMemo(() => {
    const allPlots: Set<string> = new Set();

    projectCentralRepoData.groundworks.plots.forEach((plot) => {
      if (plot.plotNo) {
        allPlots.add(plot.plotNo);
      }
    });

    return allPlots;
  }, [projectCentralRepoData]);

  const getPlotCompletionStatus = (plotNumber: string) => {
    const plot = projectCentralRepoData.groundworks.plots.find(
      (p) => p.plotNo === plotNumber
    );
    if (!plot?.stages?.length) return { isFullyCompleted: false, isPartial: false, completedTasks: 0, totalTasks: 0 };

    let totalTasks = 0;
    let completedTasks = 0;

    plot.stages.forEach((stage) => {
      stage.tasks?.forEach((task) => {
        totalTasks++;
        if (task.currentStatus === "Completed") {
          completedTasks++;
        }
      });
    });

    const isFullyCompleted = totalTasks > 0 && completedTasks === totalTasks;
    const isPartial = completedTasks > 0 && !isFullyCompleted;

    return { isFullyCompleted, isPartial, completedTasks, totalTasks };
  };

  const handleSelectAll = () => {
    handlePlotSelectionChange(availablePlotNumbers);
  };

  const handleClearAll = () => {
    handlePlotSelectionChange(new Set());
  };

  const handlePlotClick = (plot: string) => {
    const copiedSet = new Set(selectedPlots);

    if (copiedSet.has(plot)) {
      copiedSet.delete(plot);
      handlePlotSelectionChange(copiedSet);
    } else {
      copiedSet.add(plot);
      handlePlotSelectionChange(copiedSet);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-[#1e1e1e] text-lg font-semibold">
              Runs/Plots Selection
            </p>
            <p className="text-[#6b7280] text-sm">
              Click on Runs/Plots to select or deselect them
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="large"
              disabled={selectedPlots.size === availablePlotNumbers.size}
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              size="large"
              disabled={selectedPlots.size === 0}
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          </div>
        </div>
        <div className="mt-4 -mx-4 h-px bg-[#f3f4f6]" />
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 16,
          flexWrap: "wrap"
        }}
      >
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#52c41a",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Fully Completed
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#fa8c16",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Partially Completed
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#1890ff",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Selected
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#d9d9d9",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Not Started
          </Text>
        </Space>
      </div>

      <div className="flex flex-wrap gap-5">
        {[...availablePlotNumbers].map((plot) => {
          const alreadySelected = selectedPlots.has(plot);
          const { isFullyCompleted, isPartial } =
            getPlotCompletionStatus(plot);

          let backgroundColor = "#F1F3F9";
          let borderColor = "#E5E7EB";
          let textColor = "#6B7280";
          let dotColor = "#d9d9d9";

          if (isFullyCompleted) {
            backgroundColor = alreadySelected ? "#52c41a" : "#f6ffed";
            borderColor = alreadySelected ? "#52c41a" : "#b7eb8f";
            textColor = alreadySelected ? "#fff" : "#52c41a";
            dotColor = "#52c41a";
          } else if (isPartial) {
            backgroundColor = alreadySelected ? "#fa8c16" : "#fff7e6";
            borderColor = alreadySelected ? "#fa8c16" : "#ffd591";
            textColor = alreadySelected ? "#fff" : "#fa8c16";
            dotColor = "#fa8c16";
          } else if (alreadySelected) {
            backgroundColor = "#155DFC";
            borderColor = "#155DFC";
            textColor = "#fff";
            dotColor = "#1890ff";
          }

          return (
            <div
              key={plot}
              role="button"
              onClick={() => handlePlotClick(plot)}
              className="flex flex-col justify-center items-center cursor-pointer p-1.5 rounded-xl relative"
              style={{
                backgroundColor,
                border: `1px solid ${borderColor}`,
                color: textColor,
                minWidth: "75px",
                height: "59px"
              }}
            >
              {/* Status dot */}
              {!alreadySelected && (isFullyCompleted || isPartial) && (
                <span
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: dotColor
                  }}
                />
              )}
              <p className="text-lg font-semibold">{plot}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
